import React, { useState, useEffect } from 'react';
import Header from '../../Header/Header.jsx';
import Footer from '../../Footer/Footer.jsx';
import styles from './Whitedental.module.css';
import dentalVideo from '../../../images/dental.mp4';

const WhiteDentalProjectShowcase = () => {
  const [hoveredTool, setHoveredTool] = useState(null);
  const [hoveredResponsibility, setHoveredResponsibility] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('web');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const responsibilities = [
    { text: 'Content Strategy & Service Mapping', icon: '💡' },
    { text: 'UX Content Structuring', icon: '🎯' },
    { text: 'Clinical Narrative Integration', icon: '⚙️' },
    { text: 'Web Content Deployment', icon: '🌐' },
    { text: 'Patient Assurance Copy', icon: '📊' },
    { text: 'Visual & Icon Mapping Guidance', icon: '🎨' },
    { text: 'Continuous Content Refinement', icon: '🚀' }
  ];

  const tools = [
    { name: 'CMS Drafts', icon: '📝', description: 'Content Management' },
    { name: 'Figma', icon: '📊', description: 'UX Tools' },
    { name: 'Technical Copy', icon: '🔤', description: 'Frameworks' },
    { name: 'Photoshop', icon: '🖼️', description: 'Image Editor' },
    { name: 'Analytics', icon: '📈', description: 'Search Metrics' }
  ];

  const skills = [
    'HTML5', 'Responsive Content', 'Content Architecture', 'UX Writing', 
    'Dental Service Taxonomy', 'SEO Optimization', 'Healthcare Copywriting', 
    'Information Design', 'Brand Positioning'
  ];



  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <div className={styles.container}>
        {/* Animated Background */}
        <div className={styles.backgroundGradient}>
          <div className={styles.gradientOrb1}></div>
          <div className={styles.gradientOrb2}></div>
          <div className={styles.gradientOrb3}></div>
        </div>

        <div className={styles.contentWrapper}>
          {/* Hero Section */}
          <section className={styles.heroSection}>
            <div className={styles.heroContent}>
              <div className={styles.heroLeft}>
                <div className={styles.projectBadge}>
                  <span className={styles.badgeDot}></span>
                  <span>Ongoing Project</span>
                </div>
                
                <h1 className={styles.projectTitle}>Dental Service Platform</h1>
                <p className={styles.projectSubtitle}>Comprehensive Dental & Oral Care</p>
                
                <div className={styles.projectMeta}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Work</span>
                    <span className={styles.metaValue}>Website Content, Branding & Patient Experience Narrative</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Year</span>
                    <span className={styles.metaValue}>2025</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Duration</span>
                    <span className={styles.metaValue}>Ongoing</span>
                  </div>
                </div>
                
                <p className={styles.heroDescription}>
                  A clinically-driven dental practice based in East of Kailash, New Delhi, focused on delivering comprehensive, patient-centric dental care through advanced treatments, personalized plans, and decades of expert practice.
                </p>
              </div>
              
              <div className={styles.heroRight}>
                <div className={styles.heroVisual}>
                  <video 
                    className={styles.heroVideo}
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                  >
                    <source src={dentalVideo} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Introduction</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                A Dental Service Platform designed to deliver comprehensive oral healthcare by combining clinical expertise, advanced technology, and patient-focused care. The platform presents a wide range of services—from preventive treatments to cosmetic enhancements and complex restorative procedures—structured to address diverse patient needs. It emphasizes clarity, trust, and accessibility, helping patients make informed decisions while enhancing their overall treatment experience.
              </p>
            </div>
          </section>

          {/* My Role Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>My Role</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.contentCard}>
              <p className={styles.contentText}>
                My role involved shaping the platform’s digital experience by structuring content, optimizing user flow, and clearly articulating services. This included organizing treatment categories, defining clinical messaging, presenting practitioner expertise, and integrating patient-focused information to build trust and improve engagement. The goal was to create a seamless, informative, and conversion-driven experience across all digital touchpoints.
              </p>
            </div>
          </section>

          {/* Responsibilities Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Responsibilities</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.responsibilitiesGrid}>
              {responsibilities.map((responsibility, index) => (
                <div 
                  key={index}
                  className={styles.responsibilityCard}
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onMouseEnter={() => setHoveredResponsibility(index)}
                  onMouseLeave={() => setHoveredResponsibility(null)}
                >
                  <span className={styles.cardNumber}>{String(index + 1).padStart(2, '0')}</span>
                  <div className={styles.cardIcon}>{responsibility.icon}</div>
                  <div className={styles.cardText}>{responsibility.text}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Tools Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Tools Used</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.toolsGrid}>
              {tools.map((tool, index) => (
                <div 
                  key={index}
                  className={styles.toolCard}
                  style={{ animationDelay: `${index * 0.15}s` }}
                  onMouseEnter={() => setHoveredTool(index)}
                  onMouseLeave={() => setHoveredTool(null)}
                >
                  <div className={styles.toolGlow}></div>
                  <div className={styles.toolIcon}>{tool.icon}</div>
                  <div className={styles.toolName}>{tool.name}</div>
                  <div className={styles.toolDescription}>{tool.description}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Skills Section */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Technologies & Skills</h2>
              <div className={styles.titleUnderline}></div>
            </div>
            <div className={styles.skillsContainer}>
              {skills.map((skill, index) => (
                <div 
                  key={index}
                  className={styles.skillTag}
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <span className={styles.skillHash}>#</span>
                  {skill}
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <div className={styles.footerOverride}>
        <Footer  />
      </div>
    </>
  );
};

export default WhiteDentalProjectShowcase;